import React, { useEffect, useState } from "react";
import { registerApi } from "../../lib/api";
import { LogIn, LogOut, RotateCcw, Trash2, Search, Plus, X, Lock } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { Toaster } from "../../components/ui/toaster";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("nl-BE") : "—");
const fmtKm = (n) => (n == null || n === "" ? "—" : `${Number(n).toLocaleString("nl-BE")} km`);

export default function AdminRegister() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newEntry, setNewEntry] = useState({ title: "", chassis: "", km_in: "" });

  const load = () => {
    setLoading(true);
    registerApi.list().then(setItems).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const apply = (updated) =>
    setItems((list) => list.map((e) => (e.id === updated.id ? updated : e)));

  const doAction = async (id, fn, okMsg) => {
    setBusyId(id);
    try {
      const res = await fn(id);
      if (res?.id) apply(res);
      else load();
      if (okMsg) toast({ title: okMsg });
    } catch (e) {
      toast({ title: "Fout", description: e?.response?.data?.detail || String(e) });
    } finally {
      setBusyId(null);
    }
  };

  const patchField = async (id, body) => {
    try {
      const res = await registerApi.patch(id, body);
      apply(res);
    } catch (e) {
      toast({ title: "Opslaan mislukt", description: e?.response?.data?.detail || String(e) });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Dit register-item verwijderen? (enkel mogelijk als de wagen nog niet binnengekomen is)")) return;
    setBusyId(id);
    try {
      await registerApi.remove(id);
      setItems((list) => list.filter((e) => e.id !== id));
      toast({ title: "Verwijderd" });
    } catch (e) {
      toast({ title: "Niet verwijderd", description: e?.response?.data?.detail || String(e) });
    } finally {
      setBusyId(null);
    }
  };

  const handleAdd = async () => {
    if (!newEntry.title.trim()) {
      toast({ title: "Titel verplicht" });
      return;
    }
    try {
      const res = await registerApi.create({
        title: newEntry.title.trim(),
        chassis: newEntry.chassis.trim() || null,
        km_in: newEntry.km_in === "" ? null : Number(newEntry.km_in),
      });
      setItems((list) => [res, ...list]);
      setNewEntry({ title: "", chassis: "", km_in: "" });
      setAdding(false);
      toast({ title: "Toegevoegd aan register" });
    } catch (e) {
      toast({ title: "Fout", description: e?.response?.data?.detail || String(e) });
    }
  };

  const filtered = items.filter((e) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      (e.title || "").toLowerCase().includes(q) ||
      (e.chassis || "").toLowerCase().includes(q) ||
      (e.brand || "").toLowerCase().includes(q)
    );
  });

  const presentCount = items.filter((e) => e.date_in && !e.date_out).length;
  const departedCount = items.filter((e) => e.date_out).length;
  const pendingCount = items.filter((e) => !e.date_in).length;

  return (
    <div className="p-6 md:p-8" data-testid="admin-register-page">
      <Toaster />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
            Garage<span className="text-[#c5a76f]">register</span>
          </h1>
          <p className="text-white/50 text-sm">
            {items.length} wagens · <span className="text-emerald-400">{presentCount} aanwezig</span> ·{" "}
            <span className="text-white/40">{pendingCount} verwacht</span> ·{" "}
            <span className="text-red-400/80">{departedCount} vertrokken</span>
          </p>
        </div>
        <button
          onClick={() => setAdding((v) => !v)}
          data-testid="register-add-toggle"
          className="flex items-center gap-2 px-4 py-2 rounded bg-[#c5a76f] text-black text-sm font-medium hover:bg-[#d8bd86] transition-colors"
        >
          {adding ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {adding ? "Annuleren" : "Item toevoegen"}
        </button>
      </div>

      {adding && (
        <div className="mt-5 bg-[#242424] border border-white/10 rounded-md p-4 grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="register-add-form">
          <input
            value={newEntry.title}
            onChange={(e) => setNewEntry((n) => ({ ...n, title: e.target.value }))}
            placeholder="Titel / wagen"
            className="inp bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm text-white sm:col-span-2"
            data-testid="register-new-title"
          />
          <input
            value={newEntry.chassis}
            onChange={(e) => setNewEntry((n) => ({ ...n, chassis: e.target.value }))}
            placeholder="Chassisnummer"
            className="inp bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm text-white"
          />
          <div className="flex gap-2">
            <input
              type="number"
              value={newEntry.km_in}
              onChange={(e) => setNewEntry((n) => ({ ...n, km_in: e.target.value }))}
              placeholder="Km binnen"
              className="inp bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm text-white w-full"
            />
            <button onClick={handleAdd} data-testid="register-new-save" className="px-4 rounded bg-[#c5a76f] text-black text-sm font-medium">
              OK
            </button>
          </div>
        </div>
      )}

      <div className="relative mt-6 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Zoek op wagen of chassisnummer..."
          className="w-full bg-[#242424] border border-white/10 rounded pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#c5a76f]/50"
          data-testid="register-search"
        />
      </div>

      {loading ? (
        <div className="text-white/50 mt-8">Laden...</div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block mt-6 overflow-x-auto border border-white/5 rounded-md">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/40 text-xs uppercase tracking-wider bg-[#141414]">
                  <th className="px-4 py-3 font-medium">Wagen</th>
                  <th className="px-4 py-3 font-medium">Chassisnummer</th>
                  <th className="px-4 py-3 font-medium">Km binnenkomst</th>
                  <th className="px-4 py-3 font-medium">Binnen</th>
                  <th className="px-4 py-3 font-medium">Buiten</th>
                  <th className="px-4 py-3 font-medium">Reden</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Acties</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <RegisterRow
                    key={e.id}
                    entry={e}
                    busy={busyId === e.id}
                    onCheckin={() => doAction(e.id, registerApi.checkin, "Wagen aanwezig")}
                    onCheckout={() => doAction(e.id, registerApi.checkout, "Wagen vertrokken")}
                    onUndo={() => doAction(e.id, registerApi.undoCheckout, "Terug aanwezig")}
                    onDelete={() => handleDelete(e.id)}
                    onPatch={patchField}
                  />
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-white/40">
                      Geen items gevonden.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden mt-6 space-y-3">
            {filtered.map((e) => (
              <RegisterCard
                key={e.id}
                entry={e}
                busy={busyId === e.id}
                onCheckin={() => doAction(e.id, registerApi.checkin, "Wagen aanwezig")}
                onCheckout={() => doAction(e.id, registerApi.checkout, "Wagen vertrokken")}
                onUndo={() => doAction(e.id, registerApi.undoCheckout, "Terug aanwezig")}
                onDelete={() => handleDelete(e.id)}
                onPatch={patchField}
              />
            ))}
            {filtered.length === 0 && <div className="text-center py-10 text-white/40">Geen items gevonden.</div>}
          </div>
        </>
      )}
    </div>
  );
}

function StatusBadge({ entry }) {
  if (entry.date_out)
    return <span className="inline-block px-2 py-0.5 rounded text-xs bg-red-500/15 text-red-300">Vertrokken</span>;
  if (entry.date_in)
    return <span className="inline-block px-2 py-0.5 rounded text-xs bg-emerald-500/15 text-emerald-300">Aanwezig</span>;
  return <span className="inline-block px-2 py-0.5 rounded text-xs bg-white/10 text-white/50">Verwacht</span>;
}

// Inline-editable field that saves on blur / Enter
function InlineEdit({ value, onSave, placeholder, type = "text", className = "", testId, disabled = false }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => setV(value ?? ""), [value]);
  if (disabled) {
    const display = value !== undefined && value !== null && value !== "" ? value : placeholder || "—";
    return (
      <span data-testid={testId} className={`px-2 py-1 text-sm text-white/70 ${className}`}>
        {display}
      </span>
    );
  }
  const commit = () => {
    const trimmed = typeof v === "string" ? v.trim() : v;
    if (String(trimmed ?? "") !== String(value ?? "")) onSave(trimmed === "" ? null : trimmed);
  };
  return (
    <input
      type={type}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
      placeholder={placeholder}
      data-testid={testId}
      className={`bg-transparent border border-transparent hover:border-white/10 focus:border-[#c5a76f]/50 focus:bg-[#1a1a1a] rounded px-2 py-1 text-sm text-white/90 focus:outline-none w-full ${className}`}
    />
  );
}

function RowActions({ entry, busy, onCheckin, onCheckout, onUndo, onDelete }) {
  if (entry.date_out) {
    return (
      <div className="flex items-center justify-end gap-3">
        <button
          onClick={onUndo}
          disabled={busy}
          data-testid={`register-undo-${entry.id}`}
          className="flex items-center gap-1.5 text-xs text-white/50 hover:text-[#c5a76f] disabled:opacity-50"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Ongedaan
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-end gap-3">
      {!entry.date_in && (
        <button
          onClick={onCheckin}
          disabled={busy}
          data-testid={`register-checkin-${entry.id}`}
          className="flex items-center gap-1.5 text-xs font-medium text-emerald-300 hover:text-emerald-200 disabled:opacity-50"
        >
          <LogIn className="w-3.5 h-3.5" /> Aanwezig
        </button>
      )}
      {entry.date_in && (
        <button
          onClick={onCheckout}
          disabled={busy}
          data-testid={`register-checkout-${entry.id}`}
          className="flex items-center gap-1.5 text-xs font-medium text-[#c5a76f] hover:text-[#d8bd86] disabled:opacity-50"
        >
          <LogOut className="w-3.5 h-3.5" /> Vertrokken
        </button>
      )}
      {!entry.date_in && (
        <button
          onClick={onDelete}
          disabled={busy}
          data-testid={`register-delete-${entry.id}`}
          className="text-white/40 hover:text-red-400 disabled:opacity-50"
          aria-label="Verwijderen"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function RegisterRow({ entry, busy, onCheckin, onCheckout, onUndo, onDelete, onPatch }) {
  const struck = !!entry.date_out;
  return (
    <tr className={`border-t border-white/5 ${struck ? "opacity-60" : ""}`} data-testid={`register-row-${entry.id}`}>
      <td className={`px-4 py-2.5 text-white ${struck ? "line-through decoration-red-400/60" : ""}`}>
        <div className="font-medium">{entry.title}</div>
        {entry.brand && <div className="text-xs text-white/40">{entry.brand}{entry.model ? ` · ${entry.model}` : ""}</div>}
      </td>
      <td className="px-2 py-2.5">
        <InlineEdit
          value={entry.chassis}
          placeholder="—"
          testId={`register-chassis-${entry.id}`}
          onSave={(val) => onPatch(entry.id, { chassis: val })}
        />
      </td>
      <td className="px-2 py-2.5 w-32">
        <div className="flex items-center gap-1">
          <InlineEdit
            type="number"
            value={entry.km_in ?? ""}
            placeholder="—"
            disabled={entry.km_locked || !!entry.date_out}
            testId={`register-km-${entry.id}`}
            onSave={(val) => onPatch(entry.id, { km_in: val == null ? null : Number(val) })}
          />
          {(entry.km_locked || entry.date_out) && (
            <Lock className="w-3 h-3 text-white/30 shrink-0" title="Vergrendeld — kan niet meer aangepast worden" />
          )}
        </div>
      </td>
      <td className="px-4 py-2.5 text-white/70 whitespace-nowrap">{fmtDate(entry.date_in)}</td>
      <td className={`px-4 py-2.5 whitespace-nowrap ${struck ? "text-red-300/80" : "text-white/70"}`}>{fmtDate(entry.date_out)}</td>
      <td className="px-2 py-2.5 w-28">
        <InlineEdit
          value={entry.reason}
          placeholder="Verkoop"
          testId={`register-reason-${entry.id}`}
          onSave={(val) => onPatch(entry.id, { reason: val })}
        />
      </td>
      <td className="px-4 py-2.5"><StatusBadge entry={entry} /></td>
      <td className="px-4 py-2.5">
        <RowActions entry={entry} busy={busy} onCheckin={onCheckin} onCheckout={onCheckout} onUndo={onUndo} onDelete={onDelete} />
      </td>
    </tr>
  );
}

function RegisterCard({ entry, busy, onCheckin, onCheckout, onUndo, onDelete, onPatch }) {
  const struck = !!entry.date_out;
  return (
    <div className={`bg-[#242424] border border-white/5 rounded-md p-4 ${struck ? "opacity-60" : ""}`} data-testid={`register-card-${entry.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className={`text-white font-medium ${struck ? "line-through decoration-red-400/60" : ""}`}>{entry.title}</div>
        <StatusBadge entry={entry} />
      </div>
      <div className="mt-3 space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-white/40 w-24 shrink-0">Chassisnr.</span>
          <InlineEdit value={entry.chassis} placeholder="—" onSave={(val) => onPatch(entry.id, { chassis: val })} testId={`register-chassis-${entry.id}`} />
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-white/40 w-24 shrink-0">Km binnen</span>
          <InlineEdit
            type="number"
            value={entry.km_in ?? ""}
            placeholder="—"
            disabled={entry.km_locked || !!entry.date_out}
            onSave={(val) => onPatch(entry.id, { km_in: val == null ? null : Number(val) })}
            testId={`register-km-${entry.id}`}
          />
          {(entry.km_locked || entry.date_out) && <Lock className="w-3 h-3 text-white/30 shrink-0" />}
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-white/40 w-24 shrink-0">Binnen</span>
          <span className="text-white/70">{fmtDate(entry.date_in)}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-white/40 w-24 shrink-0">Buiten</span>
          <span className={struck ? "text-red-300/80" : "text-white/70"}>{fmtDate(entry.date_out)}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-white/40 w-24 shrink-0">Reden</span>
          <InlineEdit value={entry.reason} placeholder="Verkoop" onSave={(val) => onPatch(entry.id, { reason: val })} testId={`register-reason-${entry.id}`} />
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-white/5">
        <RowActions entry={entry} busy={busy} onCheckin={onCheckin} onCheckout={onCheckout} onUndo={onUndo} onDelete={onDelete} />
      </div>
    </div>
  );
}
